import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Upload, FileText, X } from "lucide-react";

interface ReceiptUploadProps {
  /** e.g. "vehicles/2019-Honda-Civic_abc123" or "business" */
  folderPath: string;
  /** e.g. "AuctionFee" */
  category: string;
  /** e.g. "2019 Honda Civic" — used in filename for clarity */
  contextLabel?: string;
  onUploaded: (url: string, filename: string) => void;
  existingUrl?: string | null;
  existingFilename?: string | null;
  onRemoved?: () => void;
}

export function ReceiptUpload({
  folderPath,
  category,
  contextLabel,
  onUploaded,
  existingUrl,
  existingFilename,
  onRemoved,
}: ReceiptUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [signedUrl, setSignedUrl] = useState<string | null>(null);

  const generateFilename = (file: File) => {
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, "0");
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const yyyy = now.getFullYear();
    const datePart = `${dd}${mm}${yyyy}`;

    const safeCat = category.replace(/[^a-zA-Z0-9]/g, "");
    const safeContext = contextLabel
      ? `_${contextLabel.replace(/[^a-zA-Z0-9]/g, "-")}`
      : "";
    const uniqueId = crypto.randomUUID().slice(0, 8);
    const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";

    return `${datePart}_${safeCat}${safeContext}_${uniqueId}.${ext}`;
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Max 10MB (MED-8)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File must be under 10MB");
      return;
    }

    // Allowed types & extensions (MED-8)
    const allowedMimeTypes = ["application/pdf", "image/jpeg", "image/png", "image/gif", "image/tiff", "image/webp"];
    const ext = file.name.split(".").pop()?.toLowerCase() || "";
    const allowedExtensions = ["pdf", "jpg", "jpeg", "png", "gif", "tiff", "webp"];

    if (!allowedMimeTypes.includes(file.type) && !allowedExtensions.includes(ext)) {
      toast.error("Unsupported file type. Only PDF and images (JPEG, PNG, GIF, WebP, TIFF) are allowed.");
      return;
    }

    setIsUploading(true);
    const filename = generateFilename(file);
    const storagePath = `${folderPath}/${filename}`;

    const { error } = await supabase.storage
      .from("receipts")
      .upload(storagePath, file, {
        cacheControl: "3600",
        upsert: false,
      });

    if (error) {
      toast.error("Failed to upload receipt");
      setIsUploading(false);
      return;
    }

    onUploaded(storagePath, filename);
    toast.success("Receipt uploaded");
    setIsUploading(false);

    // Reset input
    e.target.value = "";
  };

  const handleViewReceipt = async () => {
    if (!existingUrl) return;

    if (signedUrl) {
      window.open(signedUrl, "_blank");
      return;
    }

    const { data, error } = await supabase.storage
      .from("receipts")
      .createSignedUrl(existingUrl, 3600); // 1 hour

    if (error || !data) {
      toast.error("Failed to generate receipt link");
      return;
    }

    setSignedUrl(data.signedUrl);
    window.open(data.signedUrl, "_blank");
  };

  const handleRemove = async () => {
    if (!existingUrl) return;

    const { error } = await supabase.storage
      .from("receipts")
      .remove([existingUrl]);

    if (error) {
      toast.error("Failed to remove receipt");
      return;
    }

    setSignedUrl(null);
    onRemoved?.();
    toast.success("Receipt removed");
  };

  if (existingUrl && existingFilename) {
    return (
      <div className="flex items-center gap-2 p-2 bg-muted rounded-md text-sm">
        <FileText className="h-4 w-4 text-primary shrink-0" />
        <button
          type="button"
          onClick={handleViewReceipt}
          className="text-primary hover:underline truncate text-left"
        >
          {existingFilename}
        </button>
        {onRemoved && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0"
            onClick={handleRemove}
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="relative">
      <Input
        type="file"
        accept="image/*,application/pdf"
        capture="environment"
        onChange={handleUpload}
        disabled={isUploading}
        className="cursor-pointer file:mr-2 file:cursor-pointer"
      />
      {isUploading && (
        <div className="absolute inset-0 bg-background/80 flex items-center justify-center rounded-md">
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
          <span className="text-sm">Uploading...</span>
        </div>
      )}
    </div>
  );
}
